import React, { useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import PostregCountdown from '~/shared/components/PostregCountdown';
import usePostRegTimer from '~/shared/utils/usePostRegTimer';
import { Link } from '~/techstyle-shared/next-routes';
import { mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import Selector from '../Selector';

const DynamicFormattedMessage = FormattedMessage;

const CountDown = styled.div`
  ${mobile`
    align-self: center;
    order: 2;
  `}

  ${({ theme }) => theme.tabsNode.countdown};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.colors.stickyBannerBackground};
  // add more radius to match inner container
  border-radius: ${({ theme }) => `calc(${theme.borderRadius} + 8px)`};
  position: relative;

  &:before {
    content: '';
    z-index: -1;
    position: absolute;
    top: 0;
    display: block;
    background: transparent;
    width: 100%;
    height: 100%;
    transform: rotateY(90deg);
    transition: background 0.16s ease-in-out, transform 0.16s ease-in-out;
    background: ${({ theme }) => theme.colors.stickyBannerBackground};
    border-top: 1px solid ${({ theme }) => theme.colors.metaNavBorder};
  }

  ${({ $isMounted, $isInHeader }) =>
    $isMounted &&
    $isInHeader &&
    css`
      &:before {
        transform: rotateY(0);
      }
    `}

  ${({ theme }) => theme.tabsNode.scrolledTabsWrapper};
`;

const CountdownWrapper = ({ children }) => {
  const { pathname } = useRouter();

  // Render a link when not on post registration page
  return pathname === '/' ? (
    <CountDown>{children}</CountDown>
  ) : (
    <CountDown>
      <Link route="/" passHref>
        <a>{children}</a>
      </Link>
    </CountDown>
  );
};

CountdownWrapper.propTypes = {
  children: PropTypes.node,
};

export default function Tabs({
  bannerRef,
  handleTabSelect,
  selectedTabId,
  isDisplayText = false,
  isInHeader,
  showSelector = true,
  tabs,
  options = {},
  ...rest
}) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsMounted(true);
      // zero timeout causes a separate render so the animation actually plays
    }, 0);
    return () => {
      clearTimeout(timeout);
    };
  });

  const selectionOptions = tabs?.map((option) => {
    return {
      tab: option.id,
      content: option?.text ? (
        option.text
      ) : (
        <DynamicFormattedMessage
          id={option.tab_text_rb}
          defaultMessage={option.tab_text}
        />
      ),
    };
  });

  // allows for non-postreg pages that utilize `TabNode.Tabs` to control whether the
  // postreg timer is displayed in the header for a given page via `options.hideCountdownTimer`
  const shouldShowPostRegCountdown =
    usePostRegTimer() && !options?.hideCountdownTimer;

  return (
    <Wrapper
      ref={bannerRef}
      $isMounted={isMounted}
      $isInHeader={isInHeader}
      {...rest}
    >
      {shouldShowPostRegCountdown && (
        <CountdownWrapper>
          <PostregCountdown onPostRegPage={showSelector} />
        </CountdownWrapper>
      )}
      {showSelector && (
        <Selector
          onOptionSelect={handleTabSelect}
          selectionOptions={selectionOptions}
          selectedTabId={selectedTabId}
          isDisplayText={isDisplayText}
        />
      )}
    </Wrapper>
  );
}

Tabs.propTypes = {
  bannerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.object,
  ]),
  handleTabSelect: PropTypes.func,
  isDisplayText: PropTypes.bool,
  isInHeader: PropTypes.bool,
  options: PropTypes.object,
  selectedTabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showSelector: PropTypes.bool,
  tabs: PropTypes.array,
};
